/**
 * Smart Viewport Detection System
 * Detects actual available screen space and adjusts hero section accordingly
 * Ensures hero section fits perfectly in available space with next section below fold
 */

class SmartViewportDetection {
  constructor() {
    this.actualViewportHeight = 0;
    this.headerHeight = 64; // Header height in pixels
    this.heroMinHeight = 600; // Minimum hero height
    this.heroMaxHeight = 1000; // Maximum hero height
    this.init();
  }

  init() {
    this.detectActualViewport();
    this.adjustHeroSection();
    this.setupListeners();
  }

  detectActualViewport() {
    // Get the actual viewport height (excluding browser UI, taskbars, etc.)
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.clientHeight;
    const screenHeight = window.screen.height;
    
    // Use the most accurate measurement
    this.actualViewportHeight = Math.min(windowHeight, documentHeight);
    
    // Detect if browser UI is hidden (like when top bar is hidden)
    const browserUIHidden = windowHeight > screenHeight * 0.9;
    
    console.log(`📏 Viewport Detection:`);
    console.log(`   Window Height: ${windowHeight}px`);
    console.log(`   Document Height: ${documentHeight}px`);
    console.log(`   Screen Height: ${screenHeight}px`);
    console.log(`   Actual Viewport: ${this.actualViewportHeight}px`);
    console.log(`   Browser UI Hidden: ${browserUIHidden}`);
    
    return this.actualViewportHeight;
  }

  calculateOptimalHeroHeight() {
    // Calculate available space for hero section
    const availableHeight = this.actualViewportHeight - this.headerHeight;
    
    // Leave some buffer space (5% of viewport) for better visual separation
    const bufferSpace = this.actualViewportHeight * 0.05;
    const targetHeroHeight = availableHeight - bufferSpace;
    
    // Ensure hero height is within reasonable bounds
    const optimalHeight = Math.max(
      this.heroMinHeight,
      Math.min(this.heroMaxHeight, targetHeroHeight)
    );
    
    console.log(`🎯 Hero Height Calculation:`);
    console.log(`   Available Height: ${availableHeight}px`);
    console.log(`   Buffer Space: ${bufferSpace}px`);
    console.log(`   Target Height: ${targetHeroHeight}px`);
    console.log(`   Optimal Height: ${optimalHeight}px`);
    
    return optimalHeight;
  }

  adjustHeroSection() {
    const heroSection = document.querySelector('#home');
    if (!heroSection) return;

    const optimalHeight = this.calculateOptimalHeroHeight();
    
    // Apply the calculated height
    // Apply the calculated height
    heroSection.style.minHeight = `${optimalHeight}px`;
    // Removed strict height/max-height to allow content to grow naturally on mobile
    heroSection.style.height = 'auto';
    
    // Ensure the hero content is properly centered within this height
    const heroContent = heroSection.querySelector('.grid');
    if (heroContent) {
      heroContent.style.minHeight = `${optimalHeight - 100}px`; // Account for padding
      heroContent.style.alignItems = 'center';
    }
    
    // Update CSS custom property for other components to use
    document.documentElement.style.setProperty('--hero-optimal-height', `${optimalHeight}px`);
    document.documentElement.style.setProperty('--viewport-height', `${this.actualViewportHeight}px`);
    
    console.log(`✅ Hero section adjusted to ${optimalHeight}px`);
  }

  setupListeners() {
    // Listen for viewport changes
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        console.log('🔄 Viewport changed, recalculating...');
        this.detectActualViewport();
        this.adjustHeroSection();
      }, 250);
    });

    // Listen for orientation changes (mobile)
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        console.log('📱 Orientation changed, recalculating...');
        this.detectActualViewport();
        this.adjustHeroSection();
      }, 500); // Longer delay for orientation change
    });

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', () => {
      setTimeout(() => {
        console.log('🖥️ Fullscreen changed, recalculating...');
        this.detectActualViewport();
        this.adjustHeroSection();
      }, 100);
    });

    // Listen for visibility changes (when switching tabs or apps)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        setTimeout(() => {
          this.detectActualViewport();
          this.adjustHeroSection();
        }, 100);
      }
    });
  }

  // Public methods for manual control
  recalculate() {
    console.log('🔄 Manual recalculation triggered');
    this.detectActualViewport();
    this.adjustHeroSection();
  }

  setCustomHeight(height) {
    const heroSection = document.querySelector('#home');
    if (heroSection) {
      heroSection.style.minHeight = `${height}px`;
      heroSection.style.height = 'auto';
      console.log(`🎛️ Custom hero height set to ${height}px (min-height)`);
    }
  }

  getViewportInfo() {
    return {
      actualViewportHeight: this.actualViewportHeight,
      optimalHeroHeight: this.calculateOptimalHeroHeight(),
      headerHeight: this.headerHeight,
      availableSpace: this.actualViewportHeight - this.headerHeight
    };
  }

  // Detect if user has browser UI hidden (like hidden top bar)
  detectUIChanges() {
    const currentHeight = window.innerHeight;
    const screenHeight = window.screen.height;
    
    // If window height is very close to screen height, UI is likely hidden
    const uiHidden = currentHeight > screenHeight * 0.95;
    
    if (uiHidden) {
      console.log('🔍 Detected hidden browser UI - adjusting for extra space');
      // Recalculate with the extra space
      this.adjustHeroSection();
    }
    
    return uiHidden;
  }
}

// Create and export singleton instance
const smartViewportDetection = new SmartViewportDetection();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.smartViewportDetection = smartViewportDetection;
}

export default smartViewportDetection;